import React, { Component } from 'react';
import { initCriteo, sendCriteoEvent } from '@ma-js-common/criteo-helpers';
import { Login as LoginForm } from '@ma-myaccount/login';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import urlJoin from 'url-join';

import actions from 'actions';
import Error from 'components/Error';
import Form from 'components/Form';
import Header from 'components/Header';
import ProgressBar from 'components/ProgressBar';
import {
    DEBOUNCE_TIMEOUT,
    ESTIMA_FORM,
    FORM_ADDRESS_NAME,
    FORM_CITY_NAME,
    FORM_ZIP,
    LOGIN_INPUT_PREFIX,
    STEP_NUMBER_NAME,
} from 'constants/Global';
import { getConfig } from 'helpers/Config';
import {
    extractLoginValues,
    hasPhoneRetrieval,
    isBarometreStepNumber,
    scrollToFirstError,
} from 'helpers/FormInformations';
import { getSource } from 'helpers/Ga';
import { deepEqual, removePrefixedValues } from 'helpers/ObjectUtils';
import { getSearchParamsAsObject } from 'helpers/Referrer';
import { isLoginStep } from 'helpers/Steps';
import EstimaFormService, { getEmailFromToken } from 'services/EstimaFormService';
import AuthService, { prepareValuesToPost } from 'services/AuthService';
import { STEPS, STEPS_COUNT } from 'constants/Steps';
import { thorPushEvent, INTERACTION_EVENTS_LIST } from 'helpers/Ga4';

/**
 * Map state value to props.
 * @param {Object} state Current Redux State
 */
const mapStateToProps = (state) => ({
    pending: state.pending[actions.CREATE_ESTIMA],
    values: state.form[ESTIMA_FORM] && state.form[ESTIMA_FORM].values,
    error: state.error,
    step: state.steps,
    habitations: state.habitations,
});

/**
 * Map dispatch function to props.
 * @param {Function} dispatch Redux dispatch function
 */
const mapDispatchToProps = (dispatch) => ({
    getHabitations: () => dispatch(actions.getHabitations()),
    changeStep: (step) => dispatch(actions.changeStep(step)),
    previousStep: () => dispatch(actions.previousStep()),
    nextStep: () => dispatch(actions.nextStep()),
    stepsReady: () => dispatch(actions.stepsReady()),
    onErrorDiscard: () => dispatch(actions.clearError()),
});

/**
 * Estima Form functional Component.
 * @return {ReactElement}
 */
export class StepsFormComponent extends Component {
    /** local state **/
    constructor(props) {
        super(props);
        this.state = { loginPage: 'signup' };
    }
    /**
     * React lifecycle.
     * Go to first step.
     * Retrieve params for populating form Redux.
     */
    componentDidMount() {
        this.props.getHabitations();

        const params = getSearchParamsAsObject();

        if (!params.estima_id) {
            if (params.item_address) {
                this.props.change(FORM_ADDRESS_NAME, params.item_address);
                this.props.change('_address_name_from_url', true);
            }

            if (params.item_zip) {
                this.props.change(FORM_ZIP, params.item_zip);
                this.props.change('_zip_from_url', true);
            }

            if (params.item_city_name) {
                this.props.change(FORM_CITY_NAME, params.item_city_name);
                this.props.change('_city_name_from_url', true);
            }

            if (params.item_city_id) {
                this.props.change('_city_id_from_url', params.item_city_id);
            }

            if (params.cta_id) {
                this.props.change('cta_id', params.cta_id);
            }

            this.props.stepsReady();
        }

        if (!this.props.step.number) {
            this.props.changeStep(1);
        }

        if (getConfig('ALLOW_AUTO_FILL') && params.fill) {
            this.props.change('_filled', true);

            const valuesToFill = JSON.parse(params.fill);
            Object.keys(valuesToFill).forEach((e) => this.props.change(e, valuesToFill[e]));
        }

        getEmailFromToken().then((email) => {
            if (email) {
                this.props.change(`${LOGIN_INPUT_PREFIX}username`, email);
                this.setState({ loginPage: 'signin' });
            }
        });

        initCriteo(getConfig('CRITEO_ID'), window.user?.email);
        sendCriteoEvent({ event: 'viewPage' });
    }

    /**
     * React lifecycle.
     * Triggers async validation when form values change.
     * Scroll to top when step changes.
     * @param {Object} prevProps Previous properties
     */
    componentDidUpdate(prevProps) {
        const { asyncValidate, auth, changeStep, step, values } = this.props;

        if (
            !STEPS[step.number - 1].disableAsyncValidate &&
            !deepEqual(
                removePrefixedValues({ ...values }, LOGIN_INPUT_PREFIX),
                removePrefixedValues({ ...prevProps.values }, LOGIN_INPUT_PREFIX),
            )
        ) {
            clearTimeout(this.asyncValidationTimeout);

            /**
             * Debounce timeout identifier.
             */
            this.asyncValidationTimeout = setTimeout(() => asyncValidate(), DEBOUNCE_TIMEOUT);
        }

        if (getConfig('ALLOW_AUTO_FILL')) {
            if (prevProps.values._filled !== values._filled) {
                setTimeout(() => changeStep(7), DEBOUNCE_TIMEOUT);
            }

            if (
                values._filled &&
                prevProps.auth.contact !== auth.contact &&
                auth.contact.id === null
            ) {
                setTimeout(() => changeStep(STEPS.length), DEBOUNCE_TIMEOUT);
            }
        }

        if (prevProps.step.number !== step.number) {
            window.scrollTo(0, 0);
        }
    }

    /**
     * Handle remote error for estima.
     * @param {Object} error Error response from remote
     */
    handleEstimaError = (error, dispatch, values) => {
        if (error.response && error.response.status === 400) {
            if (!isBarometreStepNumber(values[STEP_NUMBER_NAME])) {
                scrollToFirstError();
            }

            // Displaying error information if on login step
            if (isLoginStep(values)) {
                dispatch(actions.setError(error));
            }

            const responseData = error.response.data;
            throw Object.keys(responseData).reduce((previous, current) => {
                // eslint-disable-next-line no-param-reassign
                previous[current] = [].concat(responseData[current]).join('\n');
                return previous;
            }, {});
        } else {
            const formError = {
                _error: JSON.stringify(error),
                context: 'estima form submit request responded with an unexpected error format',
            };

            dispatch(actions.setError(formError));
            throw formError;
        }
    };

    /* Estima form */
    async estimaSubmit({ values, dispatch, pageType }) {
        try {
            const { data } = await EstimaFormService.submit(values);

            thorPushEvent({
                eventType: INTERACTION_EVENTS_LIST.submitEstimationSuccess,
                payload: { form: values },
            });

            if (pageType === 'signup') {
                window.sessionStorage.setItem('first_estima', 'true');
                dispatch(actions.receivedSignup());
            }

            if (data.redirect) {
                // rate limiter redirect response
                window.location.assign(urlJoin(MA.Config.WWW_URL, data.redirect));
            }

            dispatch(actions.saveEstimaId(data.habitation_id, data.signed_username));
        } catch (error) {
            dispatch(actions.createEstimaReceived());

            if (error.response && error.response.status === 400) {
                const responseData = error.response.data;
                throw Object.keys(responseData).reduce((previous, current) => {
                    // eslint-disable-next-line no-param-reassign
                    previous[current] = [].concat(responseData[current]).join('\n');
                    return previous;
                }, {});
            } else {
                const formError = {
                    _error: JSON.stringify(error),
                    context: 'estima form submit request responded with an unexpected error format',
                };

                dispatch(actions.setError(formError));
                throw formError;
            }
        }
    }

    /* Submit form */
    async handleLoginSubmit({ values, dispatch, pageType }) {
        thorPushEvent({
            eventType: INTERACTION_EVENTS_LIST.submitContactFormAttempt,
            payload: { form: values },
        });
        try {
            const { data } = await AuthService.check();

            if (hasPhoneRetrieval({ auth: data, values })) {
                dispatch(actions.createEstimaReceived());
                dispatch(actions.nextStep());
            } else {
                thorPushEvent({
                    eventType: INTERACTION_EVENTS_LIST.submitContactFormSuccess,
                    payload: { form: values },
                });
                await this.estimaSubmit({ values, dispatch, pageType });
            }
        } catch (error) {
            const formError = {
                _error: JSON.stringify(error),
                context: 'estima login submit request responded with an unexpected error format',
            };

            dispatch(actions.setError(formError));
            throw formError;
        }
    }

    /**
     * React lifecycle.
     */
    render() {
        const {
            step,
            error,
            onErrorDiscard,
            nextStep,
            previousStep,
            values,
            formErrors,
            formHints,
            change,
            submit,
            auth,
            gaLogin,
            dispatch,
            address,
            habitations,
            pending,
        } = this.props;

        if (isBarometreStepNumber(step.number)) {
            return null;
        }

        const params = getSearchParamsAsObject();

        const isEditing = !!params.estima_id;

        const stepInfos = STEPS[step.number - 1];
        let sidePanel = null;
        if (stepInfos.sidePanel) {
            sidePanel = React.cloneElement(stepInfos.sidePanel, {
                values,
                change,
                dispatch,
                address,
            });
        }

        const formFields = prepareValuesToPost({
            ...values,
            csrf_token: window.csrfToken,
            action: 'signup',
        });

        return (
            <div className="full-height-viewport">
                <Header
                    title={STEPS[step.number - 1].title}
                    step={step.number}
                    authenticated={!auth.needLogin}
                />
                <ProgressBar step={step.number} stepCount={STEPS_COUNT} />
                {isLoginStep(step.id) ? (
                    <div className="estima__wrapper">
                        <Error error={error} discard={onErrorDiscard} />
                        <LoginForm
                            translations={{
                                signupLegalNoticeCguLinkUrl: `${getConfig('WWW_URL')}/terms`,
                                signupLegalNoticePrivacyLinkUrl: `${getConfig(
                                    'WWW_URL',
                                )}/terms/#privacy`,
                            }}
                            pageType={this.state.loginPage}
                            shouldDisplayLegalNoticeOnSignin={true}
                            env={{
                                API_URL: MA.Config.WWW_URL,
                                RECAPTCHA_PUBLIC_KEY: MA.Config.RECAPTCHA_PUBLIC_KEY,
                            }}
                            userFields={{
                                ...extractLoginValues(formFields),
                                csrf_token: window.csrfToken,
                            }}
                            isPhoneRequired={values._phoneRequired}
                            shouldDisplayRecaptcha={auth.captcha_recommended}
                            googleClientId={MA.Config.GOOGLE_CLIENT_ID}
                            ga={{
                                source: getSource(),
                                eventCategory: 'Formulaire Estima',
                                eventActionPage: 'estima',
                            }}
                            onLoginSuccess={(pageType) => {
                                this.handleLoginSubmit({ values, dispatch, pageType });
                            }}
                            onLoginError={(error) => {
                                dispatch(actions.setError(error));
                            }}
                            onReturnClick={previousStep}
                            isPositionNotFixed={false}
                            shouldDisplayBanner={false}
                            shouldDisplayOptoutCheckbox={true}
                        />
                    </div>
                ) : (
                    <Form
                        stepConfiguration={stepInfos}
                        values={values}
                        stepState={step}
                        sidePanel={sidePanel}
                        clickNext={nextStep}
                        clickPrevious={previousStep}
                        submit={submit}
                        error={error}
                        onErrorDiscard={onErrorDiscard}
                        pending={pending}
                        isEditing={isEditing}
                    >
                        {React.cloneElement(stepInfos.component, {
                            values,
                            formErrors,
                            formHints,
                            change,
                            submit,
                            dispatch,
                            address,
                            habitations,
                            auth,
                            gaLogin,
                            isEditing,
                        })}
                    </Form>
                )}
            </div>
        );
    }
}

StepsFormComponent.propTypes = {
    previousStep: PropTypes.func,
    nextStep: PropTypes.func,
    submit: PropTypes.func,
    step: PropTypes.shape({
        number: PropTypes.number,
        valid: PropTypes.bool,
    }),
    values: PropTypes.shape({}),
};

/**
 * BarometreComponent connected.
 */
export default connect(mapStateToProps, mapDispatchToProps)(StepsFormComponent);
